"use client"

import { useSearchParams, useRouter } from "next/navigation"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Checkbox } from "@/components/ui/checkbox"
import BrandListFilter from "@/components/ui/sidebar/filter-sidebar/brand-filter/brand-list-filter"
import PriceRangeFilter from "./price-range-filter"
import FilterAvailable from "@/components/ui/sidebar/filter-sidebar/filter-available"
import { useVariationFilter } from "@/hook/useVariationFilters"
import { useAttributeFilter } from "@/hook/useAttributeFilter"

import type React from "react"
import { useMemo, useCallback, useEffect, useState } from "react"
import { AttributeOptionsTitleListType, VariationOptionsTitleListType } from "@/types/api/search"

interface FilterSidebarProps {
    variationOptionsTitleList: VariationOptionsTitleListType[]
    attributeOptionsTitleList: AttributeOptionsTitleListType[]
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ variationOptionsTitleList, attributeOptionsTitleList }) => {
    const { fetchVariation, loading: loadingVariation, data: variationData } = useVariationFilter()
    const { fetchAttribute, loading: loadingAttribute, data: attributeData } = useAttributeFilter()

    const router = useRouter()
    const searchParams = useSearchParams()

    const [selectedVariations, setSelectedVariations] = useState<Set<string>>(new Set())
    const [selectedAttributes, setSelectedAttributes] = useState<Set<string>>(new Set())
    const [openVariationsAccordions, setOpenVariationsAccordions] = useState<Set<string>>(new Set())
    const [openAttributesAccordions, setOpenAttributesAccordions] = useState<Set<string>>(new Set())

    const fetchAccordionData = useCallback(
        (accordionsToOpen: Set<string>, optionsTitleList: Array<any>, fetchFunction: Function) => {
            accordionsToOpen.forEach((id) => {
                const item = optionsTitleList.find((item) => item.parent.id === id)
                if (item) {
                    fetchFunction(item.children)
                }
            })
        },
        []
    )

    useEffect(() => {
        const urlVariations = searchParams.getAll("variations[]")
        setSelectedVariations(new Set(urlVariations))

        const urlAttributes = searchParams.getAll("attributes[]")
        setSelectedAttributes(new Set(urlAttributes))

        const variationAccordionsToOpen = new Set<string>()
        variationOptionsTitleList.forEach((item) => {
            if (item.children.some((child: any) => urlVariations.includes(child.id))) {
                variationAccordionsToOpen.add(item.parent.id)
            }
        })
        setOpenVariationsAccordions(variationAccordionsToOpen)

        const attributeAccordionsToOpen = new Set<string>()
        attributeOptionsTitleList.forEach((item) => {
            if (item.children.some((child: any) => urlAttributes.includes(child.id))) {
                attributeAccordionsToOpen.add(item.parent.id)
            }
        })
        setOpenAttributesAccordions(attributeAccordionsToOpen)

        fetchAccordionData(variationAccordionsToOpen, variationOptionsTitleList, fetchVariation)
        fetchAccordionData(attributeAccordionsToOpen, attributeOptionsTitleList, fetchAttribute)
    }, [searchParams, variationOptionsTitleList, attributeOptionsTitleList, fetchVariation, fetchAttribute, fetchAccordionData])

    const handleAccordionToggle = useCallback(
        (id: string, type: 'variation' | 'attribute') => {
            const optionsTitleList = type === 'variation' ? variationOptionsTitleList : attributeOptionsTitleList
            const setOpenAccordions = type === 'variation' ? setOpenVariationsAccordions : setOpenAttributesAccordions
            const fetchFunction = type === 'variation' ? fetchVariation : fetchAttribute

            setOpenAccordions((prev) => {
                const newSet = new Set(prev)
                if (newSet.has(id)) {
                    newSet.delete(id)
                } else {
                    newSet.add(id)
                    const item = optionsTitleList.find((item) => item.parent.id === id)
                    if (item) {
                        fetchFunction(item.children)
                    }
                }
                return newSet
            })
        },
        [variationOptionsTitleList, attributeOptionsTitleList, fetchVariation, fetchAttribute]
    )

    const handleFilterChange = useCallback(
        (id: string, checked: boolean, type: 'variation' | 'attribute') => {
            const newSelected = type === 'variation' ? new Set(selectedVariations) : new Set(selectedAttributes)
            if (checked) {
                newSelected.add(id)
            } else {
                newSelected.delete(id)
            }

            if (type === 'variation') {
                setSelectedVariations(newSelected)
            } else {
                setSelectedAttributes(newSelected)
            }

            const params = new URLSearchParams(searchParams.toString())
            params.delete(type === 'variation' ? "variations[]" : "attributes[]")
            newSelected.forEach((option) => params.append(type === 'variation' ? "variations[]" : "attributes[]", option))

            router.push(`?${params.toString()}`, { scroll: false })
        },
        [searchParams, router, selectedVariations, selectedAttributes]
    )

    const renderOptions = useMemo(
        () => (options: any, type: 'variation' | 'attribute') =>
            options?.map((option: any) => {
                const isChecked = type === 'variation' ? selectedVariations.has(option?.id) : selectedAttributes.has(option?.id)

                return (
                    <label key={option?.id} className="flex items-center gap-2">
                        <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => handleFilterChange(option?.id, checked as boolean, type)}
                        />
                        {option?.title}
                    </label>
                )
            }),
        [selectedVariations, selectedAttributes, handleFilterChange]
    )

    return (
        <div className="w-full rounded-2xl  md:max-w-xs md:px-4">
            <Accordion
                type="single"
                collapsible
                onValueChange={(value) => setOpenVariationsAccordions(new Set(String(value)))}
                className="border-0"
            >
                {[
                    { value: "brand-filter", title: "برند ها", content: <BrandListFilter /> },
                    { value: "price-filter", title: "تعیین محدوده قیمت", content: <PriceRangeFilter /> },
                    { value: "available", title: "کالاهای موجود", content: <FilterAvailable /> },
                ].map(({ value, title, content }) => (
                    <AccordionItem key={value} value={value}>
                        <AccordionTrigger>{title}</AccordionTrigger>
                        <AccordionContent>{content}</AccordionContent>
                    </AccordionItem>
                ))}

                {variationOptionsTitleList?.map((item) => (
                    <AccordionItem key={item?.parent?.id} value={item?.parent?.id}>
                        <AccordionTrigger onClick={() => handleAccordionToggle(item?.parent?.id, 'variation')}>
                            {item?.parent?.title}
                        </AccordionTrigger>
                        <AccordionContent>
                            {loadingVariation ? (
                                "در حال دریافت اطلاعات..."
                            ) : (
                                <div className="flex flex-col gap-2">{renderOptions(variationData, 'variation')}</div>
                            )}
                        </AccordionContent>
                    </AccordionItem>
                ))}

                {attributeOptionsTitleList?.map((item) => (
                    <AccordionItem key={item?.parent?.id} value={item?.parent?.id}>
                        <AccordionTrigger onClick={() => handleAccordionToggle(item?.parent?.id, 'attribute')}>
                            {item?.parent?.title}
                        </AccordionTrigger>
                        <AccordionContent>
                            {loadingAttribute ? (
                                "در حال دریافت اطلاعات..."
                            ) : (
                                <div className="flex flex-col gap-2">{renderOptions(attributeData, 'attribute')}</div>
                            )}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    )
}

export default FilterSidebar
